import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Cookies from 'js-cookie';
import { GetMerchantProfile } from '../../hooks/reactQuery';
import NameForm from '../../components/landing/NameForm';
import PhoneForm from '../../components/landing/PhoneForm';
import LoginModal from '../../components/shared/LoginModal';
import CartContext from '../../Context/Cart/CartContext';
import { useBrand } from '../../Context/Brand/BrandContext';
import { getCustomerByPhone } from '../../services/customer';
import { ArrowRight, ShieldCheck, Store } from 'lucide-react';
import { usePlatformBrand } from '../../Context/PlatformBrand/PlatformBrandContext';

export default function Landing() {
  const platformBrand = usePlatformBrand();
  const { brand, loadBrand } = useBrand();
  const { clearCart } = useContext(CartContext);
  const [customerName, setCustomerName] = useState(Cookies.get('customer_name') || '');
  const [customerContact, setCustomerContact] = useState(Cookies.get('customer_phone') || '');
  const [processing, setProcessing] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [restaurantName, setRestaurantName] = useState(Cookies.get('merchant_name') || '');

  const navigate = useNavigate();
  const { merchantId, table, revenueCenter } = useParams();
  const [searchParams] = useSearchParams();
  const requestedSessionId = searchParams.get('session') || '';
  const { data: merchantData, isLoading, error } = GetMerchantProfile(merchantId);

  useEffect(() => {
    const refCode = searchParams.get('ref');
    const shoutoutToken = searchParams.get('shoutout');
    if (refCode) localStorage.setItem('referral_code', refCode);
    else localStorage.removeItem('referral_code');
    if (shoutoutToken) localStorage.setItem('shoutout_id', shoutoutToken);
    else localStorage.removeItem('shoutout_id');
  }, [searchParams]);

  useEffect(() => {
    if (merchantData?.name) {
      Cookies.set('merchant_name', merchantData.name);
      setRestaurantName(merchantData.name);
    }
    if (merchantId) {
      localStorage.setItem('merchant_id', merchantId);
      loadBrand(merchantId);
    }
    if (table) localStorage.setItem('table_number', table);
    if (revenueCenter) localStorage.setItem('revenue_center', revenueCenter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantData, merchantId]);

  const currentStep = !customerName ? 0 : 1;
  const accentColor = brand.primaryColor || '#1A7F6D';
  const restaurantLabel = restaurantName || merchantData?.name || 'Restaurant';

  const handleNameSubmit = (name) => {
    if (!name) return;
    clearCart();
    setCustomerName(name);
    Cookies.set('customer_name', name);
    localStorage.setItem('customer_name', name);
  };

  const handlePhoneChange = (contact) => {
    setCustomerContact(contact);
    Cookies.set('customer_phone', contact);
    localStorage.setItem('customer_phone', contact);
  };

  const handlePhoneSubmit = () => {
    const phone = customerContact?.replace(/\D/g, '') || '';
    const isValidPhone = phone.startsWith('256') && phone.length === 12;
    if (!isValidPhone) return false;
    setProcessing(true);

    getCustomerByPhone(merchantId, phone)
      .then(res => {
        const customer = res?.data?.customer;
        if (customer?.id) localStorage.setItem('customer_id', customer.id);
      })
      .catch(() => {});

    setTimeout(() => {
      setProcessing(false);
      navigate('/order', {
        state: {
          merchantId,
          table_number: table || '',
          revenue_center: revenueCenter || '',
          table_session_id: requestedSessionId || ''
        }
      });
    }, 450);
    return true;
  };

  const handleBack = () => {
    setCustomerName('');
    Cookies.remove('customer_name');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f7f7f5]">
        <div className="w-10 h-10 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f7f7f5] p-4">
        <p className="text-neutral-500 text-sm">Could not load this restaurant. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-950">
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col bg-[#f8f7f3]">
        <section
          className="relative overflow-hidden px-5 pb-8 pt-11 text-white"
          style={{ background: `linear-gradient(160deg, ${accentColor}, #111827)` }}
        >
          <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="absolute -bottom-24 left-8 h-52 w-52 rounded-full bg-white/10" />

          <div className="relative flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={restaurantLabel} className="h-14 w-14 rounded-2xl border border-white/20 bg-white object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15">
                  <Store className="h-6 w-6 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-lg font-semibold">{restaurantLabel}</p>
                <p className="mt-0.5 text-sm text-white/70">Order from your phone</p>
              </div>
            </div>
            {table && (
              <div className="shrink-0 rounded-full border border-white/20 bg-white/15 px-3 py-1.5 text-xs font-semibold">
                Table {table}
              </div>
            )}
          </div>

          <div className="relative mt-12">
            <h1 className="max-w-[320px] text-[38px] font-semibold leading-[1.02]">
              {currentStep === 0 ? 'Start your order.' : 'Save this visit.'}
            </h1>
            <p className="mt-4 max-w-[310px] text-[15px] leading-6 text-white/75">
              {currentStep === 0
                ? 'Tell the team who is ordering, then browse the menu.'
                : 'Use a WhatsApp number so your orders and restaurant points stay connected.'}
            </p>
          </div>
        </section>

        <main className="-mt-5 flex flex-1 items-center rounded-t-[28px] bg-[#f8f7f3] px-5 py-8">
          <div className="mx-auto w-full max-w-sm">
            <div className="mb-6 flex items-center gap-2">
              <div className="h-1 flex-1 rounded-full" style={{ backgroundColor: accentColor }} />
              <div className={`h-1 flex-1 rounded-full transition-colors ${currentStep === 1 ? '' : 'bg-neutral-200'}`} style={currentStep === 1 ? { backgroundColor: accentColor } : undefined} />
            </div>

            {!customerName ? (
              <NameForm onNameSubmit={handleNameSubmit} processing={processing} />
            ) : (
              <PhoneForm
                onPhoneSubmit={handlePhoneSubmit}
                customerContact={customerContact}
                onPhoneChange={handlePhoneChange}
                processing={processing}
                onBack={handleBack}
              />
            )}

            {!customerName && (
              <div className="mt-8 border-t border-neutral-200 pt-6">
                <button
                  onClick={() => setShowLogin(true)}
                  className="flex w-full items-center justify-between rounded-2xl bg-white px-4 py-4 text-left shadow-sm active:scale-[0.99] transition"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                      <ShieldCheck className="h-5 w-5 text-neutral-700" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-neutral-950">Use your {platformBrand.name} account</span>
                      <span className="block truncate text-xs text-neutral-500">Works across restaurants. Points stay local.</span>
                    </span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-neutral-400" />
                </button>
              </div>
            )}
          </div>
        </main>
      </div>

      {showLogin && (
        <LoginModal
          restaurantId={merchantId}
          restaurantName={restaurantName}
          onClose={() => setShowLogin(false)}
          onLoginSuccess={(userData) => {
            setShowLogin(false);
            clearCart();
            if (userData?.customer_id) localStorage.setItem('customer_id', userData.customer_id);
            if (userData?.name) {
              setCustomerName(userData.name);
              Cookies.set('customer_name', userData.name);
              localStorage.setItem('customer_name', userData.name);
            }
            if (userData?.phone) {
              setCustomerContact(userData.phone);
              Cookies.set('customer_phone', userData.phone);
              localStorage.setItem('customer_phone', userData.phone);
            }
            navigate('/order', {
              state: {
                merchantId,
                table_number: table || '',
                revenue_center: revenueCenter || '',
                table_session_id: requestedSessionId || ''
              }
            });
          }}
        />
      )}
    </div>
  );
}
